// One channel rendered as a polished card. Type badge + URL + enabled state + actions
// (copy URL, delete). Provider icon is inferred from the URL host (TestUi sink /
// Discord / generic).

import { useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Copy,
  Trash2,
  MoreHorizontal,
  Webhook,
  TestTube2,
  MessageCircle,
  CircleDot,
} from "lucide-react";
import type { AlertChannelDto, StrategyDto } from "../../api/types";
import { deleteChannel } from "../../api/channels";
import { cn } from "@/lib/utils";

interface Props {
  channel: AlertChannelDto;
  ownerStrategy: StrategyDto | undefined;
  onDeleted: () => void;
}

interface Provider {
  label: string;
  className: string;
  Icon: typeof Webhook;
}

function detectProvider(channel: AlertChannelDto): Provider {
  const url = channel.url;
  if (!url) return { label: "Unknown", className: "border-zinc-500/40 text-zinc-400", Icon: Webhook };
  if (/TestUi:8080|localhost:5180|localhost:7180|\/webhooks\/sink/.test(url)) {
    return { label: "TestUi sink", className: "border-primary/40 text-primary", Icon: TestTube2 };
  }
  if (/discord\.com\/api\/webhooks/.test(url)) {
    return { label: "Discord", className: "border-indigo-500/40 text-indigo-400", Icon: MessageCircle };
  }
  return { label: "Webhook", className: "border-zinc-500/40 text-zinc-400", Icon: Webhook };
}

export function ChannelRow({ channel, ownerStrategy, onDeleted }: Props) {
  const [busy, setBusy] = useState(false);
  const dest = channel.url ?? channel.address ?? "(no destination)";
  const provider = detectProvider(channel);

  const handleDelete = async () => {
    if (!window.confirm(`Delete this ${channel.type} channel?`)) return;
    setBusy(true);
    try {
      await deleteChannel(channel.id);
      toast.success("Channel deleted");
      onDeleted();
    } catch (err) {
      toast.error("Delete failed", { description: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(dest);
      toast.success("URL copied to clipboard");
    } catch (err) {
      toast.error("Copy failed", { description: (err as Error).message });
    }
  };

  return (
    <Card className="p-4 bg-card hover:bg-card-elevated transition-colors">
      <div className="flex items-start gap-3">
        {/* Provider chip */}
        <div className={cn(
          "grid place-items-center size-10 rounded-md border bg-card-elevated shrink-0",
          provider.className,
        )}>
          <provider.Icon className="size-4" />
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn("border", provider.className)}>
              {provider.label}
            </Badge>
            {channel.enabled ? (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-400">
                <CircleDot className="size-3 mr-1" />
                Enabled
              </Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground">
                Disabled
              </Badge>
            )}
          </div>
          <code className="block text-xs font-mono text-foreground break-all">
            {dest}
          </code>
          {ownerStrategy && (
            <div className="text-[11px] text-muted-foreground">
              attached to: <span className="text-foreground font-medium">{ownerStrategy.name}</span>
              <code className="ml-2">{ownerStrategy.id.slice(0, 8)}…</code>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" onClick={handleCopy} aria-label="Copy URL">
            <Copy className="size-3.5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="ghost" disabled={busy} aria-label="More">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCopy}>
                <Copy className="size-3.5" />
                Copy URL
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive focus:bg-destructive/10"
                onClick={handleDelete}
              >
                <Trash2 className="size-3.5" />
                Delete channel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Card>
  );
}
